import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { childrenApi } from "../../api/endpoints.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Loader } from "../../components/Loader.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";
import type { TipoConsentimiento } from "../../api/types.js";
import styles from "./Parent.module.css";

export function ConsentsPage() {
  const t = useT();
  const navigate = useNavigate();
  const { childId = "" } = useParams();
  const nino = useQuery(() => childrenApi.obtener(childId), [childId]);
  const estado = useQuery(() => childrenApi.consentimientos(childId), [childId]);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState<TipoConsentimiento | null>(null);

  async function otorgar(tipo: TipoConsentimiento, versionTextoLegal: string) {
    setError(null);
    setGuardando(tipo);
    try {
      await childrenApi.otorgarConsentimiento(childId, { tipo, versionTextoLegal });
      estado.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(null);
    }
  }

  if (nino.loading || estado.loading) {
    return (
      <AdultScreen>
        <AdultHeader title="…" />
        <Loader />
      </AdultScreen>
    );
  }

  const nombre = nino.data?.nombre ?? "";
  const audioOk = estado.data?.audio ?? false;
  const fotosOk = estado.data?.fotosOrofaciales ?? false;

  return (
    <AdultScreen>
      <AdultHeader title={t("consent.title", { name: nombre })} />
      <Content>
        {error ? <ErrorBanner message={error} /> : null}
        <InfoBanner>{t("consent.intro", { name: nombre })}</InfoBanner>

        {/* Consentimiento de AUDIO (separado) */}
        <Card>
          <div className={styles.consentRow}>
            <div className={styles.consentHead}>
              <h2>{t("consent.audio.label")}</h2>
              {audioOk ? <span className={styles.granted}>{t("consent.granted")}</span> : null}
            </div>
            <p className={styles.consentLegal}>{t("consent.audio.text")}</p>
            <span className={styles.consentVersion}>{t("consent.audio.version")}</span>
            {!audioOk ? (
              <Button
                disabled={guardando === "audio"}
                onClick={() => void otorgar("audio", t("consent.audio.version"))}
              >
                {guardando === "audio" ? t("common.saving") : t("consent.grant")}
              </Button>
            ) : null}
          </div>
        </Card>

        {/* Consentimiento de FOTOS (separado) */}
        <Card>
          <div className={styles.consentRow}>
            <div className={styles.consentHead}>
              <h2>{t("consent.fotos.label")}</h2>
              {fotosOk ? <span className={styles.granted}>{t("consent.granted")}</span> : null}
            </div>
            <p className={styles.consentLegal}>{t("consent.fotos.text")}</p>
            <span className={styles.consentVersion}>{t("consent.fotos.version")}</span>
            {!fotosOk ? (
              <Button
                variant="ghost"
                disabled={guardando === "fotos_orofaciales"}
                onClick={() => void otorgar("fotos_orofaciales", t("consent.fotos.version"))}
              >
                {guardando === "fotos_orofaciales" ? t("common.saving") : t("consent.grant")}
              </Button>
            ) : null}
          </div>
        </Card>

        {!audioOk ? <InfoBanner tone="warn">{t("consent.required.audio")}</InfoBanner> : null}

        <Button
          block
          disabled={!fotosOk}
          onClick={() => navigate(`/padre/nino/${childId}/fotos`)}
        >
          {fotosOk ? "Continuar a las fotos 📸" : "Otorga el permiso de fotos para continuar"}
        </Button>
        <Button
          variant="sun"
          block
          disabled={!audioOk}
          onClick={() => navigate(`/padre/nino/${childId}/evaluar`)}
        >
          Saltar a la evaluación 🎙️
        </Button>
        <Link to="/padre" style={{ textAlign: "center" }}>
          {t("common.back")}
        </Link>
      </Content>
    </AdultScreen>
  );
}
